// Organik Kal cart: Shopify API and shared security helpers.
(function initCartApi(global) {
    'use strict';

    const namespace = global.OrganikKalCart = global.OrganikKalCart || {};
    const mutationEndpoints = new Set(['/cart/add.js', '/cart/change.js', '/cart/clear.js']);
    const fallbackImage = 'https://cdn.shopify.com/s/images/themes/product-1.png';

    function escapeHtml(value) {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    function safeImageUrl(value) {
        if (!value) return fallbackImage;

        try {
            const url = new URL(value, global.location.origin);
            return url.protocol === 'https:' || url.origin === global.location.origin
                ? url.href
                : fallbackImage;
        } catch (_error) {
            return fallbackImage;
        }
    }

    function formatCurrency(value) {
        const amount = Number(value);
        const safeAmount = Number.isFinite(amount) ? amount : 0;
        const formattedAmount = new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(safeAmount);

        return `₺${formattedAmount}`;
    }

    function positiveInteger(value, fallback = 1) {
        const parsed = Number.parseInt(value, 10);
        return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
    }

    function setTemplateText(element, template, replacements = {}) {
        if (!element) return;

        const markerPattern = /\{\{\s*([a-z_]+)\s*\}\}/gi;
        const fragment = document.createDocumentFragment();
        let cursor = 0;
        let match;

        while ((match = markerPattern.exec(String(template)))) {
            fragment.append(document.createTextNode(String(template).slice(cursor, match.index)));
            const strong = document.createElement('strong');
            strong.textContent = String(replacements[match[1]] ?? '');
            fragment.append(strong);
            cursor = markerPattern.lastIndex;
        }

        fragment.append(document.createTextNode(String(template).slice(cursor)));
        element.replaceChildren(fragment);
    }

    async function request(endpoint, values = {}) {
        if (!mutationEndpoints.has(endpoint)) {
            throw new Error('Unsupported cart endpoint');
        }

        const response = await fetch(`${endpoint}?source=organikkal-cart`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: new URLSearchParams(values).toString(),
            credentials: 'same-origin'
        });

        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        return response.json();
    }

    async function getCart() {
        const response = await fetch(`/cart.js?source=organikkal-cart&v=${Date.now()}`, {
            credentials: 'same-origin'
        });
        if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
        return response.json();
    }

    namespace.utils = Object.freeze({
        escapeHtml,
        formatCurrency,
        positiveInteger,
        safeImageUrl,
        setTemplateText
    });

    namespace.api = Object.freeze({
        getCart,
        addItem(variantId, quantity = 1, properties = {}) {
            const id = positiveInteger(variantId, 0);
            if (!id) return Promise.reject(new Error('Invalid variant id'));

            const values = { id: String(id), quantity: String(positiveInteger(quantity)) };
            Object.entries(properties).forEach(([key, value]) => {
                values[`properties[${key}]`] = String(value);
            });
            return request('/cart/add.js', values);
        },
        changeItem(key, quantity) {
            if (!key) return Promise.reject(new Error('Invalid cart item key'));
            const safeQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
            return request('/cart/change.js', { id: String(key), quantity: String(safeQuantity) });
        },
        clearCart() {
            return request('/cart/clear.js');
        }
    });
})(window);
